import { useState, useEffect } from 'react';
import { checkIfLookFeaturesReceived } from "../../../api/look-api";
import { getCurrentLayout } from "../../appLayout";
import {
  isDevMode,
  SHOULD_DISPLAY_FEATURES_ONLY_WITH_AR3DUrls,
  SQFT_FEATURES
} from "../constants";
import { hardCodeIsHomeDepot, hardCodeIsRenuit } from "../../../misc/fabricatorHardcode";
import { matchesAllowedCombo } from "../helpers/sqft3dArHelper";
import { getNavItems } from "../helpers/sqftHelperUi";

/**
 * Standalone function to fetch features data
 * @param {Array} partIds - Array of part IDs to fetch
 * @param {string} searchText - Search filter text
 * @param {boolean} shouldGetModelsOnlyWithThirdPartyAR3DUrls - Filter by AR3D URLs
 * @param {Object} selectedFilters - Selected filters object
 * @param applyAllowedColorRule
 * @returns {Promise<Object>} Features data object
 */
export const fetchFeatures = async (
  partIds,
  searchText = "",
  selectedFilters= {},
  shouldGetModelsOnlyWithThirdPartyAR3DUrls = false,
  applyAllowedColorRule = true
) => {
  const layout = getCurrentLayout();
  const newFeaturesData = {};

  for (const partId of partIds) {
    const featureGroup = layout.getFeatureGroupByPartId(partId);
    let allNavItems = [];
    let navItems = [];
    let navItemsBeforeAR3DFilter = [];

    if (featureGroup) {
      // If features are not received and no request is in progress, wait for the promise
      if (!featureGroup.allFeaturesReceived && !featureGroup.requestInProgress) {
        await checkIfLookFeaturesReceived(featureGroup);
      }

      allNavItems = layout.designNavigationItems(
        { part: featureGroup },
        selectedFilters,
        searchText,
        null,
        "all",
        [],
        { firstFeature: null, firstConditions: null },
      );

      navItems = getNavItems(
        { part: featureGroup },
        searchText,
        selectedFilters,
        null,
        "all",
        [],
        { firstFeature: null, firstConditions: null }
      );

      // Store items before AR3D filter
      navItemsBeforeAR3DFilter = [...navItems];

      if (shouldGetModelsOnlyWithThirdPartyAR3DUrls && SHOULD_DISPLAY_FEATURES_ONLY_WITH_AR3DUrls.includes(partId)) {
        navItems = navItems.filter(feature => {
          return feature.part
            && feature.part.dbFeature
            && Array.isArray(feature.part.dbFeature.thirdPartyAR3DUrls)
            && feature.part.dbFeature.thirdPartyAR3DUrls.length > 0;
        });
      }

      if (applyAllowedColorRule && [SQFT_FEATURES.top_cabinet, SQFT_FEATURES.bottom_cabinet, SQFT_FEATURES.tall_cabinet].includes(partId)) {
        if (!isDevMode() && (hardCodeIsHomeDepot() || hardCodeIsRenuit())) {
          navItems = navItems.filter(feature => {
            const featureName = feature.part.getFeatureName?.() ?? "";
            const sharedFeatureName = feature.part.getLowerCasedSharedFeatureName?.() ?? "";
            return matchesAllowedCombo(featureName, sharedFeatureName);
          });
        }
      }
    }

    newFeaturesData[partId] = {
      rows: navItems,
      allNavItems: allNavItems,
      navItemsBeforeAR3DFilter: navItemsBeforeAR3DFilter,
      featureGroup: featureGroup,
      wasFilteredByAR3D: shouldGetModelsOnlyWithThirdPartyAR3DUrls &&
        navItemsBeforeAR3DFilter.length > 0 &&
        navItems.length === 0
    };
  }

  return newFeaturesData;
};

/**
 * React hook to fetch and manage features data with loading state
 * @param {Array} partIds - Array of part IDs to fetch
 * @param {string} searchText - Search filter text
 * @param {boolean} shouldGetModelsOnlyWithThirdPartyAR3DUrls - Filter by AR3D URLs
 * @param {Object} selectedFilters - Selected filters object
 * @param applyAllowedColorRule
 * @returns {Object} Object containing featuresData and isLoading state
 */
const useFetchFeatures = (
  partIds,
  searchText = "",
  selectedFilters,
  shouldGetModelsOnlyWithThirdPartyAR3DUrls = false,
  applyAllowedColorRule = true
) => {
  const [featuresData, setFeaturesData] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Serialize to avoid re-triggering effect on new object/array references
  const partIdsKey = JSON.stringify(partIds);
  const selectedFiltersKey = JSON.stringify(selectedFilters ?? {});

  useEffect(() => {
    let isMounted = true;

    const loadFeatures = async () => {
      setIsLoading(true);

      try {
        const data = await fetchFeatures(
          JSON.parse(partIdsKey),
          searchText,
          JSON.parse(selectedFiltersKey),
          shouldGetModelsOnlyWithThirdPartyAR3DUrls,
          applyAllowedColorRule
        );

        if (isMounted) {
          setFeaturesData(data);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error fetching features:', error);
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadFeatures();

    return () => {
      isMounted = false;
    };
  }, [partIdsKey, searchText, selectedFiltersKey, shouldGetModelsOnlyWithThirdPartyAR3DUrls, applyAllowedColorRule]);

  return { featuresData, isLoading };
};

export default useFetchFeatures;
